import React, { useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import { createInvoiceGroup } from '@atlassian/cssxp-proxies';
import { CreateBillingProfilePane, BillingProfileFormTrackerSteps } from '@css-xp/ppc-billing-profile-table';
import { useContactServicePayload } from '@css-xp/contact-resources';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import { updateShipToParty, createContactRelationship } from '@atlassian/temp-proxies';
import { failureMessage, getSupportUrl } from '@css-xp/ppc-common';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { errorNotificationBuilder, NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { BillingProfileResponse } from '../../../../types';
import './create-customer-and-billing-profile-modal.scss';

enum ModalScreen {
  CUSTOMER = 'customer',
  BILLING_PROFILE = 'billing-profile',
}

export enum ErrorStage {
  CUSTOMER_STEP = 'customer-step',
  BILLING_PROFILE_STEP = 'billing-profile-step',
  CONTACT_RELATIONSHIP_STEP = 'contact-relationship-step',
}

interface Props {
  isOpen: boolean;
  transactionAccountId: string;
  onClose: () => void;
  onSubmit: (_responsePayload?: {
    billingProfileInfo: BillingProfileResponse;
    customerName?: string | null | undefined;
    customerId?: string | undefined;
  }) => void;
  transactionAccountData?: any;
  transactionAccountDataLoading?: boolean;
}

export const getErrorMessage = (errorStage: ErrorStage) => {
  switch (errorStage) {
    case ErrorStage.CUSTOMER_STEP:
      return <>Something went wrong while creating the customer. {failureMessage()}</>;
    case ErrorStage.BILLING_PROFILE_STEP:
      return (
        <>
          Customer is created, Something went wrong while creating the billing profile. Please select the customer again
          and try to create the billing profile once more, or contact {getSupportUrl()}.
        </>
      );
    case ErrorStage.CONTACT_RELATIONSHIP_STEP:
      return (
        <>
          Customer and Billing profile created successfully, Something went wrong while creating contacts. Please visit
          the billing profile details page to add contacts or contact {getSupportUrl()}.
        </>
      );
    default:
      return failureMessage();
  }
};

export const CreateCustomerAndBillingProfileModal = ({
  isOpen,
  transactionAccountId,
  onClose,
  onSubmit,
  transactionAccountData,
  transactionAccountDataLoading,
}: Props) => {
  const [screen, setScreen] = useState(ModalScreen.CUSTOMER);
  const [customerDetails, setCustomerDetails] = useState<CommerceAddress>({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [updateBillToPartyPayload, setUpdateBillToPartyPayload] = useState<any>(undefined);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const errorStage = React.useRef(ErrorStage.CUSTOMER_STEP);

  let BillingProfileFormStages = Object.values(BillingProfileFormTrackerSteps);

  BillingProfileFormStages = BillingProfileFormStages.slice(0, BillingProfileFormStages.length - 1);
  const FormStages = ['Add customer', ...BillingProfileFormStages];
  const { getCreateContactRelationshipsPayload } = useContactServicePayload();

  const onSubmitCustomerStep = (_customerDetails: CommerceAddress, updateBillToPartyPayload: any) => {
    if (!transactionAccountDataLoading) {
      setScreen(ModalScreen.BILLING_PROFILE);
      setCustomerDetails(_customerDetails);
      setUpdateBillToPartyPayload(updateBillToPartyPayload);
    }
  };

  const onSubmitCreateBillingProfile = async (requestForm: any, contactServicePayload?: any) => {
    try {
      // create sold to address
      setIsSubmitLoading(true);
      //@ts-ignore
      const { id } = await updateShipToParty(updateBillToPartyPayload);
      if (id) {
        errorStage.current = ErrorStage.BILLING_PROFILE_STEP;
        //sending analytics event to track creation of new customer
        sendAnalyticsEvent(AnalyticsTrackAction.SUCCESSFUL, AnalyticsTrackSubject.NEW_CUSTOMER, {
          transactionAccountId,
        });
        const updatedRequestPayload = {
          data: { ...requestForm?.data, shipToPartyId: id },
          transactionAccountId: transactionAccountId,
        };
        const billingProfileResponse = await createInvoiceGroup(updatedRequestPayload);
        // create contact relationship
        if (billingProfileResponse?.id) {
          //on successfull creation of Billing Profile
          onSubmit({
            customerName: customerDetails?.name,
            customerId: id,
            billingProfileInfo: billingProfileResponse,
          });
          errorStage.current = ErrorStage.CONTACT_RELATIONSHIP_STEP;
          const createPayload = getCreateContactRelationshipsPayload({
            invoiceGroupId: billingProfileResponse?.id,
            ...contactServicePayload,
          });
          const contactServiceResponse = await createContactRelationship(
            createPayload as CreateContactRelationshipsRequest
          );
          if (contactServiceResponse) {
            setIsSubmitLoading(false);
          }
          const successMessage = successNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withId('create-invoice-group-success')
            .withHeader('Update successful')
            .withDismissAfter(5000)
            .build();
          sendSuccessNotification(successMessage);
          setNotificationSent(true);
          onClose();
        }
      }
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(getErrorMessage(errorStage.current))
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
        onClose();
      }
    }
  };

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose} testId="add-customer-modal" shouldCloseOnOverlayClick={false}>
          <div className="create-customer-billing-profile-modal">
            {screen === ModalScreen.CUSTOMER && (
              <ShipToPartySidebar
                shipToAddress={customerDetails}
                onCancelClick={onClose}
                onSubmitCustomerStep={onSubmitCustomerStep}
                onSubmitFailure={() =>
                  sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.NEW_CUSTOMER, {
                    transactionAccountId,
                  })
                }
                beforeSubmit={() =>
                  sendAnalyticsEvent(AnalyticsTrackAction.ADDED, AnalyticsTrackSubject.NEW_CUSTOMER, {
                    transactionAccountId,
                  })
                }
                transactionAccountId={transactionAccountId}
                hideAddFromBillToAddress={true}
                contentString={{
                  HEADING: 'Add customer',
                  SUBMIT_BUTTON: 'Next: Create billing profile',
                  NOTIFICATION_SUCCESS_HEADING: 'Customer added',
                }}
                FormStages={FormStages}
                isProgressIndicatorEnabled={true}
                showPricingEligibility={true}
                hideUpdateWarningMessage={true}
              />
            )}
            {screen === ModalScreen.BILLING_PROFILE && (
              <CreateBillingProfilePane
                transactionAccountId={transactionAccountId}
                transactionAccountDetails={transactionAccountData}
                isProgressIndicatorEnabled={true}
                onSubmit={onSubmitCreateBillingProfile}
                defaultSoldToAddress={customerDetails}
                isDefaultAddressSelected={!!customerDetails}
                soldToAddresses={[customerDetails]}
                addCustomerFormStages={FormStages}
                initialCurrentProgressIndex={1}
                isSubmitLoading={isSubmitLoading}
                isTermPaymentSelectionDisabled={true}
                onBackCustomerClick={() => setScreen(ModalScreen.CUSTOMER)}
              />
            )}
          </div>
        </Modal>
      )}
    </ModalTransition>
  );
};
